import { useId, useState } from 'react'

import type { AlarmEvent, AlarmState, ResourceKpi } from '@ctcells/contract'
import { alarmCategoryLabel, severityLabel, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { alarmSeverityTone, severityBadgeFlat } from '../../atoms/statusTone.ts'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { Tabs, type TabItem } from '../../layout/Tabs.tsx'
import { type ScreenAccess } from '../access.ts'
import { DEMO_ALARMS, DEMO_RESOURCE_KPI } from '../demoData.ts'
import { alarmSourceText, fullDateTimeText, hoursText, tempText, timeText } from '../format.ts'
import { OfflineNotice } from '../resourceParts.tsx'
import { severityRank } from '../sortRanks.ts'
import { withPriority } from '../listColumnPriority.ts'

import { ALL_CATEGORIES, categoryOf, categoryOptionsOf } from './alarmCategories.ts'
import {
  ackGate,
  alarmHeadingOf,
  alarmConditionText,
  alarmRowTone,
  alarmStateText,
  alarmTitleOf,
  bulkClearGate,
  bulkClearTargets,
  clearGate,
} from './alarmGates.ts'

import '../screens.css'

/**
 * 알람 센터.
 *
 * 🔴 **탭은 `state` 축이다 — 와이어프레임의 「긴급 · 조치 필요 · 자동복구 중」이 아니다**
 *    (2026-09-20 에 갈아끼웠다 · eric 지시 「와이어프레임이 정답이 아니야. api 스펙에 맞게 구성해줘」).
 *
 *    종전 탭 3종은 `EventPayload.category` 5값을 접은 것이었는데 그때(2026-09-20)는 그 필드가 실물에 없었다.
 *
 * 🔵 **2026-09-26 `category` 가 들어왔다 — 탭이 아니라 툴바의 「분류」 선택기로 가른다.**
 *    근거: `ctcells-core db/migrations/0014_alarm_active_category.sql` · `app/services/alarms.py:620`
 *    (`raise_alarm` 이 적재 시점에 카탈로그에서 채운다) · `app/views/alarms.py:97`. 실물 확인은
 *    `packages/contract/src/alarm.ts` 주석 표.
 *    - 탭(`state`)과 분류(`category`)는 **두 축**이라 한 줄에 섞지 않는다. 둘은 AND 로 거른다
 *    - 🔴 **값이 없는 알람은 「전체」에만 보인다.** 옛 적재분은 키가 안 온다(소급 불가 · `0016` 주석).
 *      「분류 없음」 선택지를 만들지 않는다 — 「모른다」를 한 분류로 접지 않는다
 *    - 선택지의 카운트는 받은 목록으로 센다 — 상태 탭과 같은 규칙(덜 받았으면 안 센다)
 *
 *    그래서 **서버가 실제로 받는 질의 축**으로 다시 짰다. `GET /alarms` 의 파라미터가
 *    `state` · `severity` · `source` 셋이고, 그중 값이 갈리는 `state` 를 탭으로 세웠다.
 *
 * ⚠ **이름을 살리려고 `severity: CRITICAL` 에 「긴급」을 갖다 붙이지 않았다.**
 *   severity 와 category 는 **다른 축**이다 — 카탈로그에 `WARN` + `category: critical` 조합이
 *   실제로 있다. 이름만 옮기면 화면이 거짓말을 한다. **이름도 축을 따라간다.**
 *
 * 🔴 **`category` 로 갈리던 자리를 실필드로 옮겼다.** 추론이 아니라 그 필드가 곧 그 뜻이다.
 *
 * | 종전 | 지금 | 근거 |
 * | --- | --- | --- |
 * | `category === 'transient'` → 「확인 불필요」 | `ack_required === false` | 그 필드의 뜻 그대로 |
 * | `category === 'critical'` → [해제] 노출 | `auto_clear === false`(latching) | `SM-ALM-05` 「미지정 = latching(자동 소멸 안 함)」 |
 * | `category === 'critical'` → 행 강조 | `severity === 'CRITICAL'` | 실필드 |
 *
 * 🔵 **「모두 해제」가 생겼다**(eric 2026-10-30 「알람 메뉴에서 오른쪽에.. 알람 해제 기능을 넣어줘. 한번에.. 알람 없애게」).
 *    종전에는 「계약에 일괄 경로가 없다」로 일괄 단추를 두지 않았다 — 경로는 지금도 건별뿐이다.
 *    그래서 부분 실패의 «어디까지 됐나»를 화면이 추론하지 않게 짰다: 건별 응답을 모아(`useClearAlarms`)
 *    «N건 해제 · M건 실패 + 까닭»을 그대로 보이고, 목록은 무효화 뒤 서버 값으로 다시 그린다(낙관적 전이 없음).
 *    확인 창에서 사람이 누를 때만 나간다(DP-02 — 자동 해제가 아니다). 「모두 확인」(ack)은 여전히 없다.
 *
 * ⚠ **「조치 가이드」를 걷었다** — `suggested_actions` 는 `EventPayload`(MQTT)에만 있고
 *   REST `Alarm` 에 없다. 화면이 문장을 만들지 않으므로 **자리도 만들지 않는다.**
 */
export type AlarmTab = 'all' | AlarmState

export interface AlarmsScreenProps {
  alarms?: readonly AlarmEvent[]
  /**
   * 서버가 답한 **총량**(`Page.total_count`). 탭 카운트의 진실성이 여기 걸린다 — 아래 주석 참조.
   * `null` = 서버가 안 줬다(총량 미상).
   */
  totalCount?: number | null
  /** 상단 KPI 4칸. 집계 엔드포인트가 없어 화면이 합성한 값이다(`ResourceKpi` 주석) */
  kpi?: ResourceKpi | null
  access?: ScreenAccess
  loading?: boolean
  onAck?: (alarmId: string) => void
  onClear?: (alarmId: string) => void
  /**
   * 오른쪽 서랍으로 상세를 여는 길(통합 콘솔 · eric 2026-09-28 「알람에서도 구조를 오른쪽으로.. 상세 페이지 보이게」).
   * 🔴 **넘기면 «서랍 모드»다** — 행 아무 칸 클릭 · 첫 칸 링크가 이것을 부르고, 줄 아래 펼침과 행 끝 [확인]·[해제]가
   *    빠진다(버튼은 서랍으로 갔다). 안 넘기면 종전 그대로(장비 관점 콘솔 · 태블릿 — 옆에 서랍 자리가 없다).
   */
  onOpenAlarm?: ((alarmId: string) => void) | undefined
  /** 서랍에 열린 알람. 그 행이 «고른 행»이다(채움 + `aria-selected`) */
  selectedAlarmId?: string | undefined
  /**
   * 「모두 해제」 — 확인 창에서 [해제]를 누르면 그때 보이던 대상 id 들과 메모를 넘긴다.
   * 안 넘기면 단추가 비활성 + 사유로 선다(숨기지 않는다).
   */
  onClearAll?: ((alarmIds: string[], note: string | undefined) => void) | undefined
  /** 일괄 해제 진행 상태 — 컨테이너가 명령 훅의 상태를 그대로 옮긴다 */
  clearAll?: BulkClearState | undefined
  /** 결과 창을 닫을 때 — 컨테이너가 명령 상태를 비운다(다음에 열 때 지난 결과가 따라오지 않게) */
  onClearAllReset?: (() => void) | undefined
}

/**
 * 일괄 해제 결과 요약. 🔴 까닭은 건별 오류 응답의 사전 문구(`errorMessage`)로 묶은 것이다 — 화면이 짓지 않는다.
 */
export interface BulkClearSummary {
  cleared: number
  failures: { reason: string; count: number }[]
}

export interface BulkClearState {
  pending: boolean
  summary?: BulkClearSummary | undefined
  /** 명령 자체가 무너졌을 때(건별 실패는 `summary.failures`) */
  error?: string | undefined
}

/** 탭 순서. 🔴 어휘는 계약 `Alarm.state` 그대로다 — 화면이 이름을 지어내지 않는다 */
const ALARM_STATES: readonly AlarmState[] = ['active', 'acknowledged', 'cleared']

/** 알람 표의 처음 정렬 — 발생 시각 최신이 위(eric 2026-09-30) */
const ALARM_DEFAULT_SORT: TableSort = { key: 'occurredAt', direction: 'desc' }

/** 상태 정렬 차례(오름차순 기준 — 내림차순이면 미확인이 위) */
const STATE_RANK: Record<AlarmState, number> = { cleared: 0, acknowledged: 1, active: 2 }

/** 조건 정렬 차례(오름차순 기준) — 값 없음 · 해소 · 유지 */
const conditionRank = (alarm: AlarmEvent): number =>
  alarm.auto_clear !== false ? 0 : alarm.resolved === true ? 1 : 2

export function AlarmsScreen({
  alarms = DEMO_ALARMS,
  totalCount = null,
  kpi = DEMO_RESOURCE_KPI,
  access = {},
  loading = false,
  onAck,
  onClear,
  onOpenAlarm,
  selectedAlarmId,
  onClearAll,
  clearAll,
  onClearAllReset,
}: AlarmsScreenProps) {
  const drawer = onOpenAlarm !== undefined
  const [tab, setTab] = useState<AlarmTab>('all')
  const [category, setCategory] = useState<string>(ALL_CATEGORIES)

  const [query, setQuery] = useState('')
  const [openKey, setOpenKey] = useState<string | null>(null)
  /* 확인 창 — 열 때 대상 id 를 «찍어 둔다». 창이 떠 있는 사이 구독 · 재조회로 목록이 바뀌어도 보내는 것은 본 그 목록이다 */
  const [bulk, setBulk] = useState<{ ids: string[]; held: number } | null>(null)
  const [bulkNote, setBulkNote] = useState('')
  const bulkNoteId = useId()

  /*
   * 🔴 **탭 카운트는 「받은 것이 전부일 때만」 센다.**
   *
   * 종전에는 `alarms.length` 를 그대로 「전체 N」으로 찍었다. 그런데 그 배열은 **한 페이지**다
   * — 서버 기본 `limit` 이 50 이라 알람이 그보다 많으면 「전체 50」이라는 **거짓말**이 뜬다
   * (실제로 실물이 105건이던 회차가 있었다).
   *
   * 그래서 서버가 답한 총량(`total_count`)과 받은 개수를 맞춰 보고, **덜 받았으면 세지 않는다.**
   * 「세지 않는다」와 「0 이다」는 다른 사실이라 그 경우 카운트 자체를 안 붙인다.
   */
  const complete = totalCount !== null && alarms.length >= totalCount
  const countOf = (predicate: (a: AlarmEvent) => boolean): number | undefined =>
    complete ? alarms.filter(predicate).length : undefined

  const tabs: TabItem<AlarmTab>[] = [
    // 🔴 전체만은 덜 받았어도 진실을 안다 — 서버가 준 총량이 그것이다
    {
      id: 'all',
      label: t('tab.alarms.all'),
      ...(totalCount === null ? {} : { count: totalCount }),
    },
    /*
     * ⚠ 라벨 키를 `tab.alarms.<state>` 로 둔다 — **`enum.alarmState` 사전을 새로 세우지 않았다.**
     *   이 세 낱말은 지금 **탭 이름으로만** 쓰이고, 표 안의 상태 표기는 `alarm.acknowledged`·
     *   `alarm.unacknowledged` 가 따로 갖고 있다. 쓰는 데가 한 곳인데 `enum.*` 을 여는 것은
     *   사전만 늘린다. 다른 화면이 같은 낱말을 쓰게 되면 그때 `enum.*` 으로 올린다.
     */
    ...ALARM_STATES.map((state): TabItem<AlarmTab> => {
      const count = countOf((a) => a.state === state)
      return {
        id: state,
        label: t(`tab.alarms.${state}`),
        ...(count === undefined ? {} : { count }),
      }
    }),
  ]

  /*
   * 분류 선택지 — 「전체」 + 어휘 전수(+ 응답에 온 모르는 값). 🔴 카운트는 **분류 축만** 센다
   * (상태 탭과 독립). 탭 카운트가 분류와 독립인 것과 짝이다 — 한쪽만 교차로 세면 두 수가 엇갈린다.
   * ⚠ 「전체」 카운트는 서버 총량이다 — 분류 없는 옛 알람까지 든 수라 다섯의 합보다 클 수 있다.
   */
  /*
   * 🔴 분류는 **작은 탭**(`Tabs variant="sub"` · 건수 배지)으로 탭 줄 오른쪽에 선다 — 시료 목록의 상태 칩과 같은 자리 · 같은 모양
   *    (eric 2026-10-12 「알람 부분 포맷이 안 맞잖아.. 기존에 우리가 수정한 탭이랑 검색이랑」). 종전 분할 막대(`Segmented`)는 걷었다.
   */
  const categoryTabs: TabItem[] = [
    {
      id: ALL_CATEGORIES,
      label: t('filter.all'),
      ...(totalCount === null ? {} : { count: totalCount }),
    },
    ...categoryOptionsOf(alarms).map((value): TabItem => {
      const count = countOf((a) => categoryOf(a) === value)
      return {
        id: value,
        label: alarmCategoryLabel(value),
        ...(count === undefined ? {} : { count }),
      }
    }),
  ]

  const needle = query.trim().toLowerCase()
  const rows = alarms.filter((alarm) => {
    if (tab !== 'all' && alarm.state !== tab) return false
    // 🔴 값 없는 알람은 어떤 분류와도 같지 않다 — 「전체」에서만 남는다
    if (category !== ALL_CATEGORIES && categoryOf(alarm) !== category) return false
    if (needle === '') return true
    return [alarm.error_code, alarm.source, alarm.message]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLowerCase().includes(needle))
  })

  /*
   * 🔴 **열 차례: 발생 시각 · 모듈 · 심각도 · 알람 · 상태**(eric 2026-09-30 「시간 모듈 심각도 알람 상태 이렇게.. 순서 바꿔줘」
   *    · 「시간을 발생 시각으로 바꿔주고.. 년월일시초 다 보여주게」 — 값은 `2026-09-30 13:05:42` 꼴).
   * 🔴 **「분류」 열은 없다** — eric 이 목록 열로 꼽지 않았다. 값은 서랍 「요약」 카드(`AlarmDetailScreen`)에 남고,
   *    거르는 것은 위 분류 선택기가 한다. 되살리지 마라.
   * 🔴 **폭: 짧은 열(발생 시각 · 모듈 · 심각도 · 상태 · 버튼)은 `fit`, 알람만 `fill`**(eric 「전체 data row 너비를 칸에 맞게」).
   *    `columnWidths="even"` 은 칸을 한 줄로 세워서 긴 서버 문구가 접히지 못하고 표가 넘친다 — 이 표에는 쓰지 않는다.
   */
  /* 🔴 대상은 «지금 거른 목록»이다 — 탭 · 분류 · 검색을 그대로 따른다(팀장 결정 2026-10-30) */
  const bulkTargets = bulkClearTargets(rows)
  const bulkButtonGate =
    onClearAll === undefined
      ? { disabled: true as const, disabledReason: t('reason.commandNotAllowed') }
      : bulkClearGate(access, bulkTargets.targets.length)
  const closeBulk = () => {
    setBulk(null)
    setBulkNote('')
    onClearAllReset?.()
  }

  const allColumns: Column<AlarmEvent>[] = [
    {
      /*
       * 🔴 **첫 열이라 서랍이 열려도 빠지지 않는다**(`columnFit` — 첫 열은 `priority` 를 줘도 안 빠진다).
       *    펼침 모드(장비 관점 콘솔 · 태블릿)에서는 이 칸이 펼침 버튼으로 감싸인다(`DataTable`).
       */
      // ⚠ `last_ts` 하나다 — `timestamp` 는 `EventPayload`(MQTT) 필드라 여기 오지 않는다
      key: 'occurredAt',
      header: t('column.alarmOccurredAt'),
      width: 'fit',
      sort: 'time',
      sortValue: (alarm) => alarm.last_ts,
      cell: (alarm) => <span className="muted mono">{fullDateTimeText(alarm.last_ts)}</span>,
    },
    {
      key: 'module',
      header: t('column.module'),
      width: 'fit',
      /*
       * 🔴 **`source` 는 모듈 «종류»다 — 어느 «대»인지는 안 온다.** 실값은 `module.centrifuge` ·
       *    `orchestrator` · `*` · `probe-module` 넷이고, 인스턴스 ID 는 `grouping_key` 뒤
       *    조각에만 있다. 사유와 한계는 `alarmSourceText()` 주석에 적어 뒀다 — 팀장 보고 대상.
       * ⚠ **`moduleLabelOf()` 로 되돌리지 마라.** 그 함수는 `:`·`-` 만 끊어서 점(`.`)이 붙은
       *   `module.centrifuge` 를 **원값 그대로 화면에 흘렸다**(2026-09-19 히나 보고).
       */
      cell: (alarm) => alarmSourceText(alarm.source),
      sort: 'text',
      sortValue: (alarm) => alarmSourceText(alarm.source),
    },
    {
      key: 'severity',
      header: t('column.severity'),
      width: 'fit',
      /* 🔴 글자 순이 아니라 **등급 순** — 내림차순이 CRITICAL 부터다. 값 없음은 칸이 그리는 대로 INFO 로 본다 */
      sort: (a, b) => severityRank(a.severity ?? 'INFO') - severityRank(b.severity ?? 'INFO'),
      cell: (alarm) => {
        const severity = alarm.severity ?? 'INFO'
        /* 경고 = 호박 · 오류 · 긴급 = 붉은색(eric 2026-10-12) — 행 왼쪽 띠 대신 이 배지가 색을 진다 */
        const tone = alarmSeverityTone(severity)
        return (
          <StatusBadge
            /* 테 없는 알약 — 작업 준비 상태 칩과 같은 규칙(eric 2026-10-30 「긴급 바깥쪽 테두리를 없애줘.. 작업 준비의 상태 칩처럼」).
               바탕 없는 정보(톤 없음)는 테를 둔다(eric 2026-10-30 「칩 테두리를 만들어줘」 · `severityBadgeFlat`) */
            flat={severityBadgeFlat(severity, tone)}
            label={severityLabel(severity)}
            status={severity}
            tone={tone}
          />
        )
      },
    },
    {
      key: 'alarm',
      header: t('column.alarm'),
      /* 남는 폭은 이 열이 받는다 — 서버 문구가 길어 여러 줄로 접히는 유일한 열이다 */
      width: 'fill',
      sort: 'text',
      sortValue: (alarm) => alarmHeadingOf(alarm),
      cell: (alarm) => {
        const title = alarmTitleOf(alarm)
        // 사전에 덮어쓴 문구가 없으면 제목이 곧 서버 문구다 — 같은 줄을 두 번 쓰지 않는다
        const sub = title === alarm.message ? '' : (alarm.message ?? '')
        const heading = alarmHeadingOf(alarm)
        return (
          <span className="cell2">
            {/*
              🔴 서랍 모드의 키보드 경로 — 진짜 `button` 이다(행 클릭은 마우스·터치용 보태기 · `DataTable` 주석).
              펼침 모드는 `DataTable` 이 첫 칸을 펼침 버튼으로 감싸므로 여기서 또 감싸지 않는다.
            */}
            {drawer ? (
              <button className="lnk" onClick={() => onOpenAlarm(alarm.alarm_id)} type="button">
                <span>{heading}</span>
              </button>
            ) : (
              <span>{heading}</span>
            )}
            <small>{sub}</small>
          </span>
        )
      },
    },
    {
      key: 'status',
      header: t('column.status'),
      width: 'fit',
      /*
       * 🔴 **이 칸은 알람 상태(`state`)만이다**(eric 2026-10-30 「상태 컬럼에 두 개가 같이 있는데 이거 분리해줘」).
       *    종전에는 latching 이면 「미확인 · 조건 유지」로 조건을 이어 썼다 — 조건은 바로 오른쪽 「조건」 칸으로 갔다.
       *
       * 🔴 **`state` 세 값을 «전부» 갈라야 한다**(2026-09-20 실물에서 잡은 틀림).
       *   종전 `state === 'active' ? 미확인 : 확인됨` 은 **이진 분기**라 `cleared` 가
       *   「확인됨」으로 떨어졌다. 실서버에 `cleared` 3건이 있어서 표 4행이 전부 「확인됨」이
       *   됐고, **같은 화면의 탭 뱃지(확인됨 1 · 해제됨 3)와 어긋났다.**
       *   ⇒ `ALARM_STATE_LABEL`(`alarmGates.ts`)로 옮겨 **빠진 값이 타입에서 걸리게** 했다.
       *   글자는 `alarmStateText()` 하나다 — 서랍의 「상태」 줄이 같은 함수를 쓴다.
       */
      cell: (alarm) => <span className="muted">{alarmStateText(alarm)}</span>,
      exportValue: (alarm) => alarmStateText(alarm),
      /* 🔴 글자 순이 아니라 **손이 급한 순** — 내림차순이 미확인(active)부터다 */
      sort: (a, b) => STATE_RANK[a.state] - STATE_RANK[b.state],
    },
    {
      key: 'condition',
      header: t('column.alarmCondition'),
      width: 'fit',
      /*
       * ⚠ 유지 · 해소는 **latching 알람(`auto_clear === false`)에서만** 값이 있다 — `resolved` 는 `state` 와 직교하고,
       *   자동 소멸하는 알람에서는 [해제] 전제가 아니라 읽는 사람을 헷갈리게 한다(07 §4.5). 그 밖에는 **빈 칸**이다(`—` 없음).
       *   (판정의 주인은 `auto_clear` 다 — `category === 'critical'` 로 되돌리지 마라.)
       */
      cell: (alarm) => <span className="muted">{alarmConditionText(alarm) ?? ''}</span>,
      exportValue: (alarm) => alarmConditionText(alarm) ?? '',
      /* 내림차순이 유지(손이 갈 것)부터 · 값 없음은 맨 끝 */
      sort: (a, b) => conditionRank(a) - conditionRank(b),
    },
    /* 🔴 서랍 모드에서는 이 열이 없다 — [확인]·[해제]는 서랍 머리로 옮겼다(eric 2026-09-28) */
    {
      key: 'actions',
      header: '',
      width: 'fit',
      align: 'end',
      /*
       * 🔴 **[확인]은 «항상 그린다». 비활성 + 사유다 — 숨기지 않는다.**
       *
       *   종전에는 `category === 'transient'` 이면 버튼 자리를 「확인 불필요」 글자로 **갈아치웠다.**
       *   그런데 `ackGate` 가 **이미** 같은 판정을 갖고 있었다(`ack_required === false` →
       *   `reason.ackNotRequired`) — 바깥 분기가 그것을 가려서 **그 사유가 한 번도 안 떴다.**
       *   판정처를 게이트 하나로 모으면 사유가 버튼의 접근 이름에 실려 **스크린리더도 읽는다.**
       *
       * ⚠ [해제]만 갈래를 둔다 — `auto_clear === false`(latching)일 때만 낸다.
       *   스스로 사라지는 알람에 영구 비활성 버튼을 달아 두는 것은 소음이다(`SM-ALM-05`
       *   「미지정 = latching(자동 소멸 안 함)」). 눌릴 수 있는지는 `clearGate` 가 답하고,
       *   `resolved` 가 아니면 **비활성 + 「조건 미해소」**다. 자동 해제는 없다(DP-02).
       */
      cell: (alarm) => (
        <span className="btnrow btnrow--end">
          <Button size="pill" {...ackGate(access, alarm)} onClick={() => onAck?.(alarm.alarm_id)}>
            {t('action.ack')}
          </Button>
          {alarm.auto_clear === false ? (
            <Button
              size="pill"
              {...clearGate(access, alarm)}
              onClick={() => onClear?.(alarm.alarm_id)}
            >
              {t('action.clearAlarm')}
            </Button>
          ) : null}
        </span>
      ),
    },
  ]
  const columns = allColumns.filter((column) => !(drawer && column.key === 'actions'))

  return (
    <ListPage
      /*
       * 🔴 **무리 탭 줄이 없다**(2026-09-22 eric 지시 「작업에서 알람탭을 따로 떼줘」).
       *    알람은 헤더의 제 문이 됐고 무리에 속하지 않는다 — 탭이 하나뿐인 무리는 줄을 안 그린다.
       *    ⇒ 위 줄은 언제나 **화면 자체 탭**(`state` 축)이고 제목도 늘 「알람」이다.
       *    `opsTab` 프롭을 되살리지 마라 — 되살리면 같은 화면으로 가는 길이 둘이 된다.
       */
      /*
       * 🔴 [모두 해제]는 제목 줄 오른쪽 끝이다(`toolbarInTabs` 라 `primaryAction` 이 머리 줄로 올라간다) — 작업 준비의 [+ 레시피]와
       *    같은 자리(eric 2026-10-30 · 처음엔 검색칸 옆이었다). **보조(외곽) 알약이다** — 되돌릴 수 없는 일괄 동작이라 주 파랑 채움으로
       *    부르지 않는다(이력의 [내보내기]와 같은 모양). 대상 0건 · 권한 없음은 비활성 + 사유
       */
      primaryAction={
        <Button
          size="pill"
          {...bulkButtonGate}
          onClick={() =>
            setBulk({
              ids: bulkTargets.targets.map((a) => a.alarm_id),
              held: bulkTargets.held.length,
            })
          }
        >
          {t('action.clearAllAlarms')}
        </Button>
      }
      onTabChange={setTab}
      tabValue={tab}
      tabs={tabs}
      tabsLabel={t('screen.alarms.title')}
      title={t('screen.alarms.title')}
      toolbar={
        <Toolbar label={t('screen.alarms.title')}>
          <Tabs
            items={categoryTabs}
            label={t('filter.category')}
            onChange={setCategory}
            value={category}
            variant="sub"
          />
          {/*
           * 눈에 보이는 글자는 「검색」뿐 — 시료 · 작업 · 이력과 같다(eric 2026-09-30). 무엇을 훑는지(코드 · 모듈 · 내용)는 접근성 이름에 남는다.
           * 🔴 「미확인만」 알약은 걷었다(eric 2026-10-12 「미확인만 버튼 없애고」) — 상태 탭 「활성」이 같은 집합이다.
           * ⚠ 죽은 칩 둘(「모듈」 · 「기간」)은 2026-09-20 에 걷었다 — `GET /alarms` 에 기간 축이 없고, 모듈(`source`)은 고를 목록을 주는
           *   경로가 서버에 없다(받은 쪽에서 긁으면 로드된 것만 고르는 거짓 셀렉트). 검색이 `source` 를 훑는다. 되살리려면 BR 이 먼저다
           */}
          <SearchInput
            label={t('filter.search.alarms')}
            onChange={setQuery}
            placeholder={t('filter.search.batchesPlaceholder')}
            value={query}
          />
        </Toolbar>
      }
      toolbarInTabs
    >
      <OfflineNotice shown={access.offline === true} />

      {bulk === null ? null : (
        <Modal
          busy={clearAll?.pending === true}
          footer={
            clearAll?.summary !== undefined ? (
              <Button onClick={closeBulk} size="pill">
                {t('action.close')}
              </Button>
            ) : (
              <>
                <Button
                  size="pill"
                  {...(clearAll?.pending === true
                    ? { disabled: true, disabledReason: t('alarm.bulkClear.sending') }
                    : {})}
                  onClick={closeBulk}
                >
                  {t('action.cancel')}
                </Button>
                <Button
                  size="pill"
                  variant="primary"
                  {...(clearAll?.pending === true
                    ? { disabled: true, disabledReason: t('alarm.bulkClear.sending') }
                    : {})}
                  onClick={() => {
                    const note = bulkNote.trim()
                    onClearAll?.(bulk.ids, note === '' ? undefined : note)
                  }}
                >
                  {clearAll?.pending === true
                    ? t('alarm.bulkClear.sending')
                    : t('action.clearAlarm')}
                </Button>
              </>
            )
          }
          onClose={closeBulk}
          size="sm"
          title={t('alarm.bulkClear.title')}
        >
          {clearAll?.summary !== undefined ? (
            <div aria-live="polite">
              <p>
                {t('alarm.bulkClear.resultCleared', { count: clearAll.summary.cleared })}
                {clearAll.summary.failures.length === 0
                  ? ''
                  : ` · ${t('alarm.bulkClear.resultFailed', {
                      count: clearAll.summary.failures.reduce((n, f) => n + f.count, 0),
                    })}`}
              </p>
              {clearAll.summary.failures.length === 0 ? null : (
                <>
                  {/* 목록 표지 없는 묶음 — `ul` 의 글머리표가 `.vissue` 상자 밖으로 나가 떠서 역할로 목록을 말한다 */}
                  <div
                    aria-label={t('alarm.bulkClear.failuresLabel')}
                    className="vissue"
                    role="list"
                  >
                    {clearAll.summary.failures.map((f) => (
                      <span key={f.reason} role="listitem">
                        {t('alarm.bulkClear.failureItem', { reason: f.reason, count: f.count })}
                      </span>
                    ))}
                  </div>
                  <p className="muted">{t('alarm.bulkClear.failuresRemain')}</p>
                </>
              )}
              {bulk.held === 0 ? null : (
                <p className="muted">{t('alarm.bulkClear.held', { count: bulk.held })}</p>
              )}
            </div>
          ) : (
            <>
              <p>{t('alarm.bulkClear.confirm', { count: bulk.ids.length })}</p>
              {bulk.held === 0 ? null : (
                <p className="muted">{t('alarm.bulkClear.held', { count: bulk.held })}</p>
              )}
              <Field htmlFor={bulkNoteId} label={t('field.alarmNote')}>
                <TextInput
                  id={bulkNoteId}
                  onChange={setBulkNote}
                  readOnly={clearAll?.pending === true}
                  value={bulkNote}
                  wide
                />
              </Field>
              {clearAll?.error === undefined ? null : (
                <p className="vissue" role="alert">
                  {clearAll.error}
                </p>
              )}
            </>
          )}
        </Modal>
      )}

      {kpi ? <ResourceKpiCards kpi={kpi} /> : null}

      {/* ⚠ 강조 축은 `severity` 다 — `category: critical` 이 아니다(두 축이 다르다) */}
      <DataTable
        caption={t('screen.alarms.title')}
        /*
         * 🔴 **처음 차례: 발생 시각(`last_ts`) 내림차순 — 최신이 맨 위**(eric 2026-09-30 「발생 시각 순으로 정렬해서
         *    보여줘」). 머리글을 누르면 다른 열 · 오름차순으로 바뀐다(`DataTable` 정렬 · 「게시판 전부」 지시).
         *    상태로 먼저 묶지 않는다 — 탭(`state`)이 이미 가르고, 「전체」 탭은 통째로 시각 순이다.
         * ⚠ **서버가 시각 순으로 주지 않는다.** `GET /alarms` 에 정렬 인자가 없고(`openapi.json` 실측 — `limit` ·
         *   `offset` · `state` · `severity` · `source` · `category`), 서버는 `alarm_id`(uuid4)로 늘어놓는다
         *   (`ctcells-core app/services/alarms.py` `list_` 의 `.order_by(_table.c.alarm_id)`) — 사실상 무작위다.
         *   그래서 **받은 목록 안에서** 화면이 줄 세운다. 지금은 상한(`ALARM_PAGE_LIMIT` 500)으로 전부 받지만, 넘으면
         *   받은 것이 «최신 N건»이 아니라 «아무 N건»이 된다 — 서버 정렬 요청 건(`docs/backend-requests.csv` BR-109).
         */
        defaultSort={ALARM_DEFAULT_SORT}
        /* 🔴 공간이 모자라면 덜 중요한 열부터 빠진다 — 차례는 `listColumnPriority.ts` 한 곳 */
        columns={withPriority('alarms', columns)}
        /*
         * 🔴 굵게 하지 않는다(eric 2026-10-12 「알림에서 글자 두께가 두꺼운 것이 많은데.. 다 통일 시켜줘」) — 종전에는 알람 칸 문구가
         *    전부 `<b>`(semibold)이고 긴급 행은 행 전체를 `emphasis` 로 또 굵혔다. 긴급은 심각도 배지(글리프 + 글자)와 활성이면
         *    위험 띠(`rowTone`)가 말한다 — 굵기로 한 번 더 말하지 않는다. `emphasis` 를 되살리지 마라.
         */
        emptyLabel={t('state.empty.alarms')}
        /* 해제된 행만 글자를 흐리게 — 왼쪽 띠는 걷었다(`alarmRowTone`) */
        rowTone={alarmRowTone}
        loading={loading}
        rowKey={(alarm) => alarm.alarm_id}
        rows={rows}
        {...(drawer
          ? {
              /* 🔴 행 아무 칸이나 눌러도 열린다 — 다른 서랍 목록과 같은 규약(eric 2026-09-23) */
              onRowClick: (alarm: AlarmEvent) => onOpenAlarm(alarm.alarm_id),
              selected: (alarm: AlarmEvent) => alarm.alarm_id === selectedAlarmId,
            }
          : {
              expandedKey: openKey,
              onToggleExpand: (key: string) =>
                setOpenKey((current) => (current === key ? null : key)),
              renderExpanded: (alarm: AlarmEvent) => <AlarmDetail alarm={alarm} />,
            })}
      />
    </ListPage>
  )
}

/**
 * 펼친 행 — 발생처 · 발생 시각 · 확인자. **펼침 모드(장비 관점 콘솔 · 태블릿) 전용**이다 —
 * 통합 콘솔은 오른쪽 서랍(`AlarmDetailScreen`)이 상세를 그린다. 🔴 에러 코드 자체는 화면에 쓰지 않는다(§1-1).
 *
 * ⚠ **「조치 가이드」(`suggested_actions`) 칸은 없다** — `EventPayload`(MQTT) 필드라 L1 에 오지 않는다.
 * 🔴 **「분류」는 여기 적는다** — 2026-09-30 목록에서 「분류」 열을 뺐다(eric). 펼침 모드에는 서랍이 없어
 *   여기 없으면 값이 갈 곳이 없다. 값 없음은 목록 때처럼 `—`(서버가 안 줬다).
 */
function AlarmDetail({ alarm }: { alarm: AlarmEvent }) {
  return (
    <div className="expand">
      <ValueRows
        rows={[
          {
            key: 'category',
            label: t('column.category'),
            value: alarmCategoryLabel(categoryOf(alarm)),
          },
          { key: 'module', label: t('column.module'), value: alarmSourceText(alarm.source) },
          {
            key: 'occurred',
            label: t('column.occurredAt'),
            value: timeText(alarm.last_ts),
          },
          {
            key: 'ack',
            label: t('column.actor'),
            value: alarm.ack_by ?? t('mask.none'),
          },
        ]}
      />
    </div>
  )
}

/** 상단 KPI 4칸 — 폐기통 · 팁 · 시약 여유 · 시약 보관 온도 (2350행) */
function ResourceKpiCards({ kpi }: { kpi: ResourceKpi }) {
  const wasteWarn = kpi.waste_pct >= kpi.waste_threshold_pct

  return (
    <div className="cards cards--4" style={{ marginBottom: 'var(--space-2xl)' }}>
      <div className={`card kpi${wasteWarn ? ' kpi--warn' : ''}`}>
        <span className="kpi__label">{t('kpi.waste')}</span>
        <span className="kpi__value">
          {`${String(kpi.waste_pct)} %`}
          <small>{t('kpi.threshold', { value: `${String(kpi.waste_threshold_pct)} %` })}</small>
        </span>
      </div>
      <div className="card kpi">
        <span className="kpi__label">{t('kpi.tip')}</span>
        <span className="kpi__value">
          {String(kpi.tip_remaining)}
          <small>{t('kpi.capacity', { value: String(kpi.tip_capacity) })}</small>
        </span>
      </div>
      <div className="card kpi">
        <span className="kpi__label">{t('kpi.reagentHeadroom')}</span>
        <span className="kpi__value">
          {hoursText(kpi.reagent_headroom_sec)}
          <small>{t('kpi.required', { value: hoursText(kpi.reagent_required_sec) })}</small>
        </span>
      </div>
      <div className="card kpi">
        <span className="kpi__label">{t('kpi.reagentStorage')}</span>
        <span className="kpi__value">
          {tempText(kpi.reagent_storage_celsius)}
          <small>
            {t('kpi.setpoint', { value: tempText(kpi.reagent_storage_setpoint_celsius) })}
          </small>
        </span>
      </div>
    </div>
  )
}
